import { CustomFieldType, isPhoneValid } from '@nova/core';

type ValidationRule = (value: unknown) => boolean | string;

/** Options for creating custom field validation rules (third argument to createCustomFieldRules). */
export interface CreateCustomFieldRulesOptions {
  required: boolean;
  minLengthOrValue?: number | null;
  maxLengthOrValue?: number | null;
}

const messages = {
  required: (fieldLabel: string) => `${fieldLabel} is required`,
  invalidEmail: 'Invalid email',
  invalidPhone: 'Invalid phone',
  minLength: (min: number) => `Minimum ${min} characters`,
  maxLength: (max: number) => `Maximum ${max} characters`,
  minValue: (min: number) => `Minimum value is ${min}`,
  maxValue: (max: number) => `Maximum value is ${max}`
};

function isValidEmail(value: string): boolean {
  if (!value || typeof value !== 'string') return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

/**
 * This is a pure function that returns Vuetify-compatible validation rules.
 * Used by the useCustomFieldValidation composable and can be used directly
 * where needed.
 *
 * @param fieldLabel - The field label for error messages
 * @param type - The custom field type (from CustomFieldType enum)
 * @param options - Object containing required flag and optional min/max constraints
 * @returns Array of validation functions compatible with Vuetify :rules prop
 */
export function createCustomFieldRules(
  fieldLabel: string,
  type: CustomFieldType,
  options: CreateCustomFieldRulesOptions
): Array<ValidationRule> {
  const { required, minLengthOrValue, maxLengthOrValue } = options;

  const rules: Array<ValidationRule> = [];

  // Required validation — Vuetify expects each rule to return true (boolean) or a string.
  if (required) {
    const requiredMsg = messages.required(fieldLabel);
    rules.push((v: unknown): boolean | string => {
      if (Array.isArray(v)) {
        return v.length > 0 ? true : requiredMsg;
      }
      if (typeof v === 'boolean') {
        return true;
      }
      if (typeof v === 'number') {
        return Number.isFinite(v) ? true : requiredMsg;
      }
      return v ? true : requiredMsg;
    });
  }

  // Type-specific validations
  if (type === CustomFieldType.Email) {
    rules.push((v: unknown): boolean | string => {
      if (!v) return true;
      return typeof v === 'string' && isValidEmail(v) ? true : messages.invalidEmail;
    });
  }

  if (type === CustomFieldType.Phone) {
    rules.push((v: unknown): boolean | string => {
      if (!v) return true;
      return typeof v === 'string' && isPhoneValid(v) ? true : messages.invalidPhone;
    });
  }

  // Length/range validations (use != null to treat both undefined and null as "no constraint")
  if (minLengthOrValue != null || maxLengthOrValue != null) {
    if (type === CustomFieldType.String || type === CustomFieldType.BigString) {
      if (minLengthOrValue != null) {
        const minLen = minLengthOrValue;
        const minLenMsg = messages.minLength(minLen);
        rules.push((v: unknown): boolean | string => {
          if (!v) return true;
          return typeof v === 'string' && v.length >= minLen ? true : minLenMsg;
        });
      }
      if (maxLengthOrValue != null) {
        const maxLen = maxLengthOrValue;
        const maxLenMsg = messages.maxLength(maxLen);
        rules.push((v: unknown): boolean | string => {
          if (!v) return true;
          return typeof v === 'string' && v.length <= maxLen ? true : maxLenMsg;
        });
      }
    } else if (type === CustomFieldType.Number) {
      if (minLengthOrValue != null) {
        const minVal = minLengthOrValue;
        const minValMsg = messages.minValue(minVal);
        rules.push((v: unknown): boolean | string => {
          if (v === '' || v == null) return true;
          return Number(v) >= minVal ? true : minValMsg;
        });
      }
      if (maxLengthOrValue != null) {
        const maxVal = maxLengthOrValue;
        const maxValMsg = messages.maxValue(maxVal);
        rules.push((v: unknown): boolean | string => {
          if (v === '' || v == null) return true;
          return Number(v) <= maxVal ? true : maxValMsg;
        });
      }
    }
  }

  return rules;
}
